import { useState, useCallback } from 'react';

/**
 * A set of picked ids for multi-select (Closing bulk delete, Review, the item mover).
 *   toggle(id)      add / remove one
 *   toggleAll(ids)  pick all of them — or, when all are picked already, none of them
 *   clear()         pick nothing
 *   keepOnly(ids)   drop picks that no longer exist (deleted / handled elsewhere)
 * Every update that changes nothing keeps the same Set, so it doesn't re-render.
 */
export function useSelection() {
  const [selected, setSelected] = useState(() => new Set());
  const toggle = useCallback(id => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  }), []);
  const toggleAll = useCallback(ids => setSelected(s => {
    if (!ids.length) return s;
    const all = ids.every(id => s.has(id));
    const n = new Set(s);
    ids.forEach(id => (all ? n.delete(id) : n.add(id)));
    return n;
  }), []);
  const clear = useCallback(() => setSelected(s => (s.size ? new Set() : s)), []);
  const keepOnly = useCallback(ids => setSelected(s => {
    if (!s.size) return s;
    const live = ids instanceof Set ? ids : new Set(ids);
    const n = new Set([...s].filter(id => live.has(id)));
    return n.size === s.size ? s : n;
  }), []);
  return { selected, toggle, toggleAll, clear, keepOnly };
}
